import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchArticles, formatDate } from '../../api';
import usePaginationWithURL from '../../hooks/usePaginationWithURL';
import { PlusIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import createContentIcon from '../../img/create-content.svg';
import Pagination from '../../ui/molecules/Pagination';
import { DeleteConfirmationModal } from '../../ui/atoms';
import StandardTable from '../../ui/molecules/StandardTable';
import TableActions from '../../ui/molecules/TableActions';
import TablePageLayout from '../../ui/molecules/TablePageLayout';
import useSessionContext from '../../hooks/useSessionContext';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { getDisplayArticleStatus, getArticleFailureReason } from './articleStatus';

const mapArticleForTable = (article) => ({
  link: article.id,
  title: article.title,
  slug: article.slug,
  date: formatDate(article.date_created),
  status: getDisplayArticleStatus(article),
  failureReason: getArticleFailureReason(article),
  queueStatus: article.queue_status || null,
  owner: article.user_created?.first_name || article.user_created?.email || 'Unknown',
  workspace: article.article_linked_workspace?.workspace_name || null,
  type: article.content_type || 'N/A',
  project: article.project?.project_name || '-'
});

function ArticlesTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Use standardized pagination hook
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalCount,
    setTotalCount,
    handlePageChange,
    handleItemsPerPageChange
  } = usePaginationWithURL({
    defaultItemsPerPage: 25,
    allowedItemsPerPage: [10, 25, 50, 100],
    scrollToTop: true,
    scrollOffset: 0
  });
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { token, userId, workspaceId: resolvedWorkspaceId } = useSessionContext();

  const primaryClick = useCallback(() => navigate('/writer/articles/create-article/step/setup'), [navigate]);

  // Checkbox functions
  const handleSelectArticle = useCallback((articleId) => {
    setSelectedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedArticles.size === articles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(articles.map(article => article.link)));
    }
  }, [articles, selectedArticles.size]);

  // Show delete modal
  const handleDeleteClick = useCallback(() => {
    if (selectedArticles.size === 0) return;
    setShowDeleteModal(true);
  }, [selectedArticles.size]);

  // Bulk delete function
  const handleBulkDelete = useCallback(async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    
    try {
      const deletePromises = Array.from(selectedArticles).map(async (articleId) => {
        const baseURL = window.location.hostname === 'localhost' ? '/directus' : 'https://panel.prosecaptain.com';
        const response = await fetch(`${baseURL}/items/articles/${articleId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to delete article ${articleId}`);
        }
        return articleId;
      });

      await Promise.all(deletePromises);
      const deletedCount = selectedArticles.size;
      
      setArticles(prev => prev.filter(article => !selectedArticles.has(article.link)));
      setTotalCount(prev => Math.max(0, prev - deletedCount));
      setSelectedArticles(new Set());
      
      console.log(`Successfully deleted ${selectedArticles.size} articles`);
    } catch (error) {
      console.error('Error deleting articles:', error);
      alert('Error deleting some articles. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedArticles, token, setTotalCount]);

  // Download function - converts article to DOCX
  const handleDownloadClick = useCallback(async () => {
    if (selectedArticles.size === 0) {
      alert('Please select at least one article to download');
      return;
    }

    if (selectedArticles.size > 1) {
      alert('Please select only one article at a time to download');
      return;
    }

    const articleId = Array.from(selectedArticles)[0];
    const article = articles.find(a => a.link === articleId);
    
    if (!article) {
      alert('Article not found');
      return;
    }

    try {
      // Fetch full article data
      const baseURL = window.location.hostname === 'localhost' ? '/directus' : 'https://panel.prosecaptain.com';
      const response = await fetch(`${baseURL}/items/articles/${articleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch article data');
      }

      const articleData = await response.json();
      const data = articleData.data;

      // Get article content (prefer article_body, fallback to article_draft)
      let content = data.article_body || data.article_draft;
      const articleTitle = data.title || 'Article';

      if (!content) {
        alert('Article has no content to download');
        return;
      }

      // Parse content if it's a string
      let outputData;
      if (typeof content === 'string') {
        try {
          outputData = JSON.parse(content);
        } catch {
          // If not JSON, create a simple structure
          outputData = {
            blocks: [
              {
                type: 'paragraph',
                data: { text: content }
              }
            ]
          };
        }
      } else {
        outputData = content;
      }

      // Ensure outputData has blocks
      if (!outputData.blocks || !Array.isArray(outputData.blocks)) {
        outputData = {
          blocks: [
            {
              type: 'paragraph',
              data: { text: typeof content === 'string' ? content : JSON.stringify(content) }
            }
          ]
        };
      }

      // Helper function to extract plain text from HTML
      const extractText = (html) => {
        if (!html) return '';
        return html
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .trim();
      };

      // Helper function to parse text with bold/italic formatting
      const parseFormattedText = (html) => {
        if (!html) return [];
        const text = extractText(html);
        const parts = [];
        let lastIndex = 0;
        const boldRegex = /<b>(.*?)<\/b>|<strong>(.*?)<\/strong>/gi;
        let match;

        while ((match = boldRegex.exec(html)) !== null) {
          if (match.index > lastIndex) {
            const beforeText = extractText(html.substring(lastIndex, match.index));
            if (beforeText) {
              parts.push(new TextRun(beforeText));
            }
          }
          const boldText = match[1] || match[2];
          if (boldText) {
            parts.push(new TextRun({
              text: extractText(boldText),
              bold: true
            }));
          }
          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < html.length) {
          const remainingText = extractText(html.substring(lastIndex));
          if (remainingText) {
            parts.push(new TextRun(remainingText));
          }
        }

        if (parts.length === 0) {
          return [new TextRun(text)];
        }

        return parts;
      };

      // Convert EditorJS blocks to DOCX paragraphs
      const docxChildren = [];
      
      outputData.blocks.forEach(block => {
        switch (block.type) {
          case 'header':
            const headerText = extractText(block.data.text);
            if (headerText) {
              let headingLevel;
              switch (block.data.level) {
                case 1: headingLevel = HeadingLevel.HEADING_1; break;
                case 2: headingLevel = HeadingLevel.HEADING_2; break;
                case 3: headingLevel = HeadingLevel.HEADING_3; break;
                case 4: headingLevel = HeadingLevel.HEADING_4; break;
                case 5: headingLevel = HeadingLevel.HEADING_5; break;
                case 6: headingLevel = HeadingLevel.HEADING_6; break;
                default: headingLevel = HeadingLevel.HEADING_2;
              }
              docxChildren.push(
                new Paragraph({
                  text: headerText,
                  heading: headingLevel,
                  spacing: { after: 200 }
                })
              );
            }
            break;
          case 'paragraph':
            const paragraphText = block.data.text;
            if (paragraphText) {
              const textParts = parseFormattedText(paragraphText);
              docxChildren.push(
                new Paragraph({
                  children: textParts,
                  spacing: { after: 200 }
                })
              );
            }
            break;
          case 'list':
            block.data.items.forEach((item) => {
              const itemText = extractText(item);
              if (itemText) {
                const textParts = parseFormattedText(item);
                docxChildren.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: block.data.style === 'ordered' ? '• ' : '• ',
                        bold: true
                      }),
                      ...textParts
                    ],
                    spacing: { after: 100 },
                    indent: { left: 400 }
                  })
                );
              }
            });
            break;
          case 'quote':
            const quoteText = extractText(block.data.text);
            if (quoteText) {
              docxChildren.push(
                new Paragraph({
                  text: `"${quoteText}"`,
                  spacing: { after: 200 },
                  indent: { left: 400 },
                  italics: true
                })
              );
            }
            break;
          case 'table':
            if (block.data.content && block.data.content.length > 0) {
              block.data.content.forEach((row) => {
                const rowText = row.map(cell => extractText(cell)).join('\t');
                docxChildren.push(
                  new Paragraph({
                    text: rowText,
                    spacing: { after: 100 }
                  })
                );
              });
              docxChildren.push(
                new Paragraph({
                  text: '',
                  spacing: { after: 200 }
                })
              );
            }
            break;
          default:
            if (block.data.text) {
              const plainText = extractText(block.data.text);
              if (plainText.trim()) {
                docxChildren.push(
                  new Paragraph({
                    text: plainText,
                    spacing: { after: 200 }
                  })
                );
              }
            }
        }
      });

      // Prepare document children
      const documentChildren = [];
      
      // Add thumbnail image if available
      if (data.article_thumbnail_url) {
        try {
          // Handle both old format (string) and new format (array)
          let thumbnailUrl = null;
          if (typeof data.article_thumbnail_url === 'string') {
            thumbnailUrl = data.article_thumbnail_url;
          } else if (Array.isArray(data.article_thumbnail_url) && data.article_thumbnail_url.length > 0) {
            // Find chosen thumbnail or use first one
            const chosenThumbnail = data.article_thumbnail_url.find(thumb => thumb.chosen);
            thumbnailUrl = chosenThumbnail ? chosenThumbnail.url : data.article_thumbnail_url[0].url;
          }
          
          if (thumbnailUrl) {
            // Fetch the image
            const imageResponse = await fetch(thumbnailUrl);
            if (imageResponse.ok) {
              const imageBlob = await imageResponse.blob();
              const imageArrayBuffer = await imageBlob.arrayBuffer();
              
              // Get image dimensions for proper sizing (max width 500px, maintain aspect ratio)
              const img = new Image();
              const imageUrl = URL.createObjectURL(imageBlob);
              await new Promise((resolve, reject) => {
                img.onload = () => {
                  URL.revokeObjectURL(imageUrl);
                  const maxWidth = 500;
                  const aspectRatio = img.height / img.width;
                  const width = Math.min(img.width, maxWidth);
                  const height = width * aspectRatio;
                  
                  documentChildren.push(
                    new Paragraph({
                      children: [
                        new ImageRun({
                          data: imageArrayBuffer,
                          transformation: {
                            width: width,
                            height: height,
                          },
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 400 },
                    })
                  );
                  resolve();
                };
                img.onerror = () => {
                  URL.revokeObjectURL(imageUrl);
                  reject(new Error('Failed to load image'));
                };
                img.src = imageUrl;
              });
            }
          }
        } catch (imageError) {
          console.warn('Could not add thumbnail to document:', imageError);
          // Continue without thumbnail if there's an error
        }
      }
      
      // Add title
      documentChildren.push(
        new Paragraph({
          text: articleTitle,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 }
        })
      );
      
      // Add content
      documentChildren.push(...docxChildren);
      
      // Create DOCX document
      const doc = new Document({
        sections: [{
          properties: {},
          children: documentChildren
        }]
      });

      // Generate and download DOCX file
      const blob = await Packer.toBlob(doc);
      const fileName = `${articleTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
      saveAs(blob, fileName);
      
      console.log('✅ Document downloaded successfully');
    } catch (error) {
      console.error('❌ Error generating DOCX:', error);
      alert('Failed to generate document. Please try again.');
    }
  }, [selectedArticles, articles, token]);

  // Removed handleSendToWP - no longer used

  const fetchCurrentPage = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);

    try {
      const data = await fetchArticles(token, userId, currentPage, resolvedWorkspaceId, itemsPerPage);
      setArticles(data.data.map(mapArticleForTable));

      // Use filtered count from Directus metadata for accurate server-side pagination.
      const serverCount = data.meta?.filter_count ?? data.meta?.total_count ?? data.data.length;
      setTotalCount(serverCount);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [currentPage, itemsPerPage, token, userId, resolvedWorkspaceId, setTotalCount]);

  useEffect(() => {
    fetchCurrentPage({ showLoading: true });
  }, [fetchCurrentPage]);

  const hasActiveArticles = useMemo(
    () => articles.some((article) => article.status === 'ongoing'),
    [articles]
  );

  useEffect(() => {
    if (!hasActiveArticles) return undefined;

    let refreshInProgress = false;
    const refresh = async () => {
      if (refreshInProgress || document.visibilityState === 'hidden') return;
      refreshInProgress = true;
      try {
        await fetchCurrentPage();
      } finally {
        refreshInProgress = false;
      }
    };

    const intervalId = window.setInterval(refresh, 2000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCurrentPage, hasActiveArticles]);

  // Override the hook's handlePageChange to also clear selected articles
  const handlePageChangeWithClearSelection = useCallback((newPage) => {
    handlePageChange(newPage);
    setSelectedArticles(new Set());
  }, [handlePageChange]);

  const handleRowClick = useCallback(async (article) => {
    if (article.status === 'ongoing') {
      return;
    }
    
    if (article.status === 'draft' || article.status === 'failed') {
      try {
        const baseURL = window.location.hostname === 'localhost' ? '/directus' : 'https://panel.prosecaptain.com';
        const response = await fetch(`${baseURL}/items/articles/${article.link}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const articleData = await response.json();
          const data = articleData.data;
          
          let targetStep = 'setup';
          
          if (data.content_type) {
            targetStep = 'setup';
          }
          if (data.article_context) {
            targetStep = 'keywords';
          }
          if (data.keywords && data.keywords.length > 0) {
            targetStep = 'review';
          }
          if (data.article_draft && data.article_draft !== '{}') {
            targetStep = 'review';
          }
          
          navigate(`/writer/articles/create-article/step/${targetStep}?id=${article.link}`);
        } else {
          navigate(`/writer/articles/create-article/step/review?id=${article.link}`);
        }
      } catch (error) {
        console.error('Error determining draft step:', error);
        navigate(`/writer/articles/create-article/step/review?id=${article.link}`);
      }
    } else {
      navigate(`/writer/articles/${article.link}`);
    }
  }, [navigate, token]);

  const EmptyState = React.memo(({ onPrimaryClick }) => (
    <div className="flex flex-col items-center justify-center h-full text-center py-24">
      <img 
        src={createContentIcon} 
        alt={t('articles.createNew')} 
        className="mx-auto h-32 w-32 text-primary-400"
      />
      <h1 className="text-3xl font-medium text-gray-900 mt-6">{t('articles.emptyState.title')}</h1>
      <p className="mt-1 text-base text-gray-800 opacity-80">{t('articles.emptyState.description')}</p>
      <div className="mt-6">
        <button
          onClick={onPrimaryClick}
          className="btn btn-primary btn-md btn-rounded"
        >
          <PlusIcon className="h-5 w-5 mr-2" aria-hidden="true" />
          {t('articles.emptyState.button')}
        </button>
      </div>
    </div>
  ));

  const truncateTitle = (title, maxLength) => {
    if (title.length <= maxLength) return title;
    return title.slice(0, maxLength).trim() + '...';
  };

  const formatArticleTypeLabel = (type) => {
    if (!type) return '-';

    const normalized = String(type).trim().toLowerCase();
    const typeMap = {
      blogpost: 'Article',
      summary: 'Summary',
      changelog: 'Changelog'
    };

    if (typeMap[normalized]) return typeMap[normalized];

    return normalized
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Definicja kolumn dla StandardTable
  const columns = useMemo(() => [
    {
      key: 'title',
      title: t('navigation.articles'),
      render: (article) => (
        <div className="font-medium text-gray-900">{truncateTitle(article.title, 50)}</div>
      ),
    },
    {
      key: 'type',
      title: t('common.type'),
      render: (article) => (
        <span className="text-sm text-gray-700">
          {formatArticleTypeLabel(article.type)}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('common.status'),
      render: (article) => (
        <div className="max-w-[360px]">
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              article.status === 'completed' || article.status === 'success' ? 'bg-green-100 text-green-800' :
              article.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
              article.status === 'published' ? 'bg-blue-100 text-blue-800' :
              article.status === 'failed' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}
            title={article.status === 'failed' && article.failureReason ? article.failureReason : undefined}
          >
            {t(`articles.status.${article.status}`)}
            {article.status === 'ongoing' && (
              <svg className="animate-spin ml-1 mt-1 mr-0 h-4 w-4 text-yellow-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
          </span>
          {article.status === 'failed' && article.failureReason && (
            <div className="mt-1 text-[11px] text-red-700 truncate" title={article.failureReason}>
              {article.failureReason}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'owner',
      title: t('common.owner'),
    },
    {
      key: 'date',
      title: t('common.creationDate'),
    },
  ], [t]);

  // Przygotowanie danych z dodatkowymi właściwościami
  const tableData = useMemo(() => {
    return articles.map(article => ({
      ...article,
      id: article.link,
      disabled: article.status === 'ongoing',
    }));
  }, [articles]);

  return (
    <TablePageLayout
      title={t('articles.title')}
      description={t('articles.description')}
      primaryAction={t('articles.createNew')}
      onPrimaryAction={primaryClick}
    >
      <div className="mb-4">
        <TableActions
          actions={[
            {
              label: isDeleting ? t('common.deleting') : t('common.delete'),
              icon: TrashIcon,
              onClick: handleDeleteClick,
              disabled: selectedArticles.size === 0 || isDeleting,
              loading: isDeleting,
              variant: 'outline',
            },
            {
              label: t('common.download'),
              icon: ArrowDownTrayIcon,
              onClick: handleDownloadClick,
              disabled: selectedArticles.size !== 1,
            },
          ]}
        />
      </div>

      <StandardTable
        columns={columns}
        data={tableData}
        onRowClick={handleRowClick}
        loading={loading}
        SkeletonRow={SkeletonRow}
        EmptyState={() => <EmptyState onPrimaryClick={primaryClick} />}
        selectable={true}
        selectedItems={selectedArticles}
        onSelectItem={handleSelectArticle}
        onSelectAll={handleSelectAll}
        firstColumnTitle={t('navigation.articles')}
        getRowClassName={(article) =>
          article.status === 'ongoing' ? 'opacity-60 cursor-not-allowed' : ''
        }
      />
      {!loading && articles.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChangeWithClearSelection}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
      
      <DeleteConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        selectedCount={selectedArticles.size}
        isDeleting={isDeleting}
      />
    </TablePageLayout>
  );
}

const SkeletonRow = React.memo(() => (
  <tr className="animate-pulse">
    <td className="px-0 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-3/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
    </td>
    <td className="px-6 py-6 whitespace-nowrap">
      <div className="h-8 bg-gray-200 rounded w-1/2"></div>
    </td>
  </tr>
));

export default ArticlesTable;